import type { AudioFrame } from "@/utils/arcade/beat";
import { Fx } from "./fx";
import { createProgression } from "./progression";
import { createRhythmChart, hitGain, leadFor, missCost } from "./rhythmChart";
import { drawBeatFlash, drawCoverBackdrop, drawGlow, drawLightRays, drawSpectrumRing, drawVinyl, glowText, withAlpha } from "./render";
import { createFeedbackQueue, createSpawnWatcher, EMPTY_INPUT, tapInput, type FrameInput, type GameEngine, type GameEnv, type GameStatus } from "./types";

interface Pad {
  x: number;
  y: number;
  due: number;
  number: number;
  color: string;
  judged: boolean;
}

interface Mark {
  x: number;
  y: number;
  life: number;
  miss: boolean;
}

const PERFECT_SECONDS = 0.07;
const GOOD_SECONDS = 0.17;
const HIT_KEYS = new Set(["Space", "KeyZ", "KeyX", "KeyD", "KeyF", "KeyJ", "KeyK", "Enter"]);

// Tap rhythm game around the cover art. Pads bloom on the beat along a
// wandering path, an approach ring closes in on each one, and you tap as the
// ring lands. The path swings wider when the song gets louder.
export function createPulsePads(env: GameEnv): GameEngine {
  const { theme, rng, difficulty, art } = env;
  const { palette } = theme;
  const fx = new Fx(rng, env.reducedMotion, art, env.quiet);
  const progression = createProgression(fx, palette, ["Chords: two pads at once", "Streams between the beats", "Everything, faster"]);
  let width = env.width;
  let height = env.height;

  let time = 0;
  const judgeTime = () => time - (env.controls?.timingOffsetMs ?? 0) / 1000;
  let score = 0;
  let combo = 0;
  let health = 1;
  let over = false;
  let pads: Pad[] = [];
  let marks: Mark[] = [];
  let walkAngle = rng() * Math.PI * 2;
  let walkRadius = 0.32;
  let counter = 0;
  const chart = createRhythmChart(rng, difficulty);
  let lastLevel = 1;
  const judgement = { label: "", life: 0, color: palette.text };
  const watcher = createSpawnWatcher();
  const feedbackQueue = createFeedbackQueue();
  let damage = 0;

  const approach = () => leadFor(1.35, progression.level, difficulty, 0.75);
  const base = () => Math.min(width, height);
  const center = () => ({ x: width / 2, y: height * 0.54 });
  const padRadius = () => Math.max(env.controls?.largeTargets ? 28 : 20, Math.min(48, base() * (env.controls?.largeTargets ? 0.085 : 0.065)));

  const place = (audio: AudioFrame, at: number) => {
    const c = center();
    let best = { x: c.x, y: c.y };
    let bestGap = -1;
    // Walk on from the last pad, but never onto a pad that will still be on
    // screen: two overlapping pads cannot both be read or hit.
    for (let attempt = 0; attempt < 6; attempt++) {
      const angle = walkAngle + (0.55 + rng() * 0.7) * (rng() < 0.2 ? -1 : 1) * (attempt % 2 === 0 ? 1 : -1);
      const radius = Math.max(0.25, Math.min(0.42, walkRadius + (rng() - 0.5) * 0.12 + (audio.energy - 0.4) * 0.05));
      // Spread over the whole stage, wide or tall, but never onto the record.
      const rx = Math.min(width * 0.42, width * radius * 1.05);
      const ry = Math.min(height * 0.34, height * radius * 0.85);
      const spot = { x: c.x + Math.cos(angle) * rx, y: c.y + Math.sin(angle) * ry };
      const clearance = base() * 0.12 * 1.35 + padRadius() * 1.2;
      const fromCenter = Math.hypot(spot.x - c.x, spot.y - c.y);
      if (fromCenter < clearance) {
        const push = clearance / Math.max(1, fromCenter);
        spot.x = c.x + (spot.x - c.x) * push;
        spot.y = c.y + (spot.y - c.y) * push;
      }
      let gap = Infinity;
      for (const pad of pads) if (!pad.judged && Math.abs(pad.due - at) < approach()) gap = Math.min(gap, Math.hypot(pad.x - spot.x, pad.y - spot.y));
      if (gap > bestGap) {
        bestGap = gap;
        best = spot;
        walkAngle = angle;
        walkRadius = radius;
      }
      if (gap > padRadius() * 3) break;
    }
    return best;
  };

  const spawn = (audio: AudioFrame, due: number, chord = false) => {
    counter = (counter % 9) + 1;
    const at = place(audio, due);
    const color = progression.fever ? palette.accent : palette.lanes[counter % palette.lanes.length];
    pads.push({ ...at, due, number: counter, color, judged: false });
    if (chord) {
      const c = center();
      pads.push({ x: c.x * 2 - at.x, y: c.y * 2 - at.y, due, number: counter, color, judged: false });
    }
  };

  const judge = (label: string, color: string) => {
    judgement.label = label;
    judgement.color = color;
    judgement.life = 0.5;
  };

  const hit = (pad: Pad) => {
    pad.judged = true;
    const perfect = Math.abs(pad.due - judgeTime()) <= PERFECT_SECONDS;
    combo += 1;
    health = Math.min(1, health + hitGain(perfect, progression.level));
    feedbackQueue.push("tap");
    const multiplier = Math.min(4, 1 + Math.floor(combo / 10) * 0.5) * progression.multiplier;
    score += Math.round((perfect ? 100 : 60) * multiplier);
    fx.burst(pad.x, pad.y, pad.color, perfect ? 24 : 14, perfect ? 260 : 170, 0);
    fx.ring(pad.x, pad.y, pad.color, padRadius() * 3);
    judge(perfect ? "PERFECT" : "GOOD", perfect ? palette.accent : palette.text);
  };

  const earliestDue = () => {
    let best: Pad | null = null;
    for (const pad of pads) if (!pad.judged && Math.abs(pad.due - judgeTime()) <= GOOD_SECONDS && (!best || pad.due < best.due)) best = pad;
    return best;
  };

  return {
    bloom: 0.55,

    resize(nextWidth, nextHeight) {
      const sx = nextWidth / Math.max(1, width);
      const sy = nextHeight / Math.max(1, height);
      width = nextWidth;
      height = nextHeight;
      for (const pad of pads) {
        pad.x *= sx;
        pad.y *= sy;
      }
    },

    autopilot() {
      const due = pads.filter((candidate) => !candidate.judged && Math.abs(candidate.due - judgeTime()) < 0.02);
      if (due.length === 0) return EMPTY_INPUT;
      const input = tapInput(due[0].x, due[0].y);
      return { ...input, taps: due.map((pad) => ({ x: pad.x, y: pad.y })) };
    },

    update(dt, input: FrameInput, audio) {
      judgement.life = Math.max(0, judgement.life - dt);
      for (const mark of marks) mark.life -= dt;
      marks = marks.filter((mark) => mark.life > 0);
      if (over) {
        fx.update(dt);
        return;
      }
      time += dt;

      if (audio.beat) {
        // Chart the beat one approach ahead so each ring closes on a beat.
        const tickAt = time - audio.sinceBeat / 1000;
        for (const step of chart.onBeat(tickAt, audio.beatPeriod / 1000, approach(), progression.level, audio.energy, audio.musicCue?.(Math.ceil(approach() / (audio.beatPeriod / 1000)) * audio.beatPeriod / 1000))) spawn(audio, step.at, step.chord);
      }
      progression.update(dt, time, combo);
      if (progression.level !== lastLevel) {
        lastLevel = progression.level;
        feedbackQueue.push("level");
      }

      const reach = padRadius() * 1.35;
      for (const tap of input.taps) {
        let best: Pad | null = null;
        for (const pad of pads) {
          if (pad.judged || Math.abs(pad.due - judgeTime()) > GOOD_SECONDS) continue;
          if (Math.hypot(tap.x - pad.x, tap.y - pad.y) > reach) continue;
          if (!best || pad.due < best.due) best = pad;
        }
        if (best) hit(best);
      }
      for (const code of input.pressed) {
        if (!HIT_KEYS.has(code)) continue;
        const pad = earliestDue();
        if (pad) hit(pad);
      }

      for (const pad of pads) {
        if (!pad.judged && judgeTime() - pad.due > GOOD_SECONDS) {
          pad.judged = true;
          combo = 0;
          health -= missCost(progression.level);
          damage += 1;
          feedbackQueue.push("hurt");
          marks.push({ x: pad.x, y: pad.y, life: 0.6, miss: true });
          judge("MISS", palette.danger);
          fx.kick(4);
        }
      }
      pads = pads.filter((pad) => !pad.judged);
      if (health <= 0) {
        health = 0;
        over = true;
      }
      fx.update(dt);
    },

    draw(ctx, audio) {
      drawCoverBackdrop(ctx, art, palette, width, height, { alpha: 0.55, drift: time * 0.18 });
      const c = center();
      drawLightRays(ctx, c.x, c.y, 18, -time * 0.08, palette.accent, 0.025 + audio.pulse * 0.035, Math.hypot(width, height) * 0.55);
      ctx.save();
      fx.applyShake(ctx);

      const coreR = base() * 0.12 * (1 + audio.pulse * 0.06);
      drawSpectrumRing(ctx, audio, c.x, c.y, coreR * 1.08, coreR * 0.7, time * 0.3, [palette.accent, palette.primary]);
      drawVinyl(ctx, art, palette, c.x, c.y, coreR, time * 1.2, 1);

      const upcoming = pads.filter((pad) => pad.due - time <= approach()).sort((a, b) => a.due - b.due);
      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      ctx.lineCap = "round";
      for (let i = 1; i < upcoming.length; i++) {
        const a = upcoming[i - 1];
        const b = upcoming[i];
        ctx.strokeStyle = withAlpha(b.color, 0.35);
        ctx.lineWidth = 3;
        ctx.setLineDash([2, 10]);
        ctx.lineDashOffset = -time * 40;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
      }
      ctx.restore();

      const r = padRadius();
      for (const pad of [...upcoming].reverse()) {
        const remaining = pad.due - time;
        const progress = 1 - Math.max(0, remaining) / approach();
        const appear = Math.min(1, progress * 3);
        ctx.save();
        ctx.globalAlpha = appear;
        drawGlow(ctx, art, pad.color, pad.x, pad.y, r * 2.6, 0.6 + (progress > 0.85 ? 0.4 : 0));
        const fill = ctx.createRadialGradient(pad.x - r * 0.3, pad.y - r * 0.3, 0, pad.x, pad.y, r);
        fill.addColorStop(0, "#fff");
        fill.addColorStop(0.35, pad.color);
        fill.addColorStop(1, withAlpha(pad.color, 0.55));
        ctx.fillStyle = fill;
        ctx.beginPath();
        ctx.arc(pad.x, pad.y, r * (0.85 + appear * 0.15), 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = "#fff";
        ctx.lineWidth = 3;
        ctx.stroke();
        ctx.fillStyle = palette.backgroundDeep;
        ctx.font = `800 ${Math.round(r * 0.8)}px Hanson, Poppins, sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(String(pad.number), pad.x, pad.y + 1);
        const ring = r * (1 + Math.max(0, remaining) / approach() * 2.2);
        ctx.globalCompositeOperation = "lighter";
        ctx.strokeStyle = withAlpha(pad.color, 0.9);
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(pad.x, pad.y, ring, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }

      for (const mark of marks) {
        ctx.save();
        ctx.globalAlpha = Math.min(1, mark.life * 2);
        ctx.strokeStyle = palette.danger;
        ctx.lineWidth = 5;
        ctx.lineCap = "round";
        const s = r * 0.5;
        ctx.beginPath();
        ctx.moveTo(mark.x - s, mark.y - s);
        ctx.lineTo(mark.x + s, mark.y + s);
        ctx.moveTo(mark.x + s, mark.y - s);
        ctx.lineTo(mark.x - s, mark.y + s);
        ctx.stroke();
        ctx.restore();
      }

      if (judgement.life > 0 && !env.quiet) {
        glowText(ctx, judgement.label, c.x, c.y + coreR + 42, Math.min(30, width * 0.065), judgement.color, Math.min(1, judgement.life * 3));
        if (combo > 2) glowText(ctx, `${combo} COMBO`, c.x, c.y + coreR + 72, 14, palette.primary, Math.min(1, judgement.life * 3));
      }

      fx.draw(ctx);
      ctx.restore();
      fx.drawBanner(ctx, width, height);
      drawBeatFlash(ctx, width, height, palette.accent, audio.pulse * (progression.fever ? 0.9 : 0.5) + fx.flash);
    },

    feedback() {
      return feedbackQueue.drain();
    },

    probe() {
      for (const pad of pads) watcher.see("pad", pad);
      return { threats: pads.filter((pad) => !pad.judged && pad.due - time <= approach()).length, pickups: 0, spawned: watcher.spawned, damage, intensity: chart.density(time) * (1.35 / approach()), boss: false, mission: null };
    },

    status(): GameStatus {
      return { score, lives: 0, maxLives: 0, combo, meter: health, over, level: progression.level, fever: progression.fever };
    },
  };
}
